import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  CssBaseline,
  useMediaQuery,
  useTheme,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { bookmakerLogos } from '../assets/bookmakerLogos';

// Static bet types - Added Summary
const betTypes = ['Total', 'Spread', 'Moneyline', 'Summary'];

// Static period types per sport
const periodTypesBySport = {
  NBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
    CBB: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  WNBA: [
    'Full Game',
    'First Half',
    'Second Half',
    'First Quarter',
    'Second Quarter',
    'Third Quarter',
    'Fourth Quarter',
  ],
  MLB: ['Full Game'],
  NHL: ['Full Game', 'First Period', 'Second Period', 'Third Period'],
  NFL: ['Full Game', 'First Half', 'Second Half','First Quarter','Second Quarter','Third Quarter','Fourth Quarter'],
  CFB: ['Full Game', 'First Half', 'Second Half','First Quarter','Second Quarter','Third Quarter','Fourth Quarter'],
};

// Static list of bookmakers in specified order
const staticBookmakers = [
  'DraftKings',
  'Pinnacle',
  'Bookmaker',
  'Circa',
  'FanDuel',
  'Bet365',
  'ESPN BET',
  'Caesars',
  'BetMGM',
  'BetRivers',
  'Bovada',
  'Fliff',
  'Hard Rock',
  'Prophet Exchange',
  'NoVig',
    'Polymarket',
  'Kalshi',
  'Bet105',
];

// Bookmaker links configuration
const bookmakerLinks = {
  'Prophet Exchange': 'https://prophetx.onelink.me/E5Yi/kxa7a4q7',
  'NoVig': 'https://novig.onelink.me/JHQQ/1vgeffy0',
  'DraftKings': 'https://dksb.sng.link/As9kz/85o9?_dl=https%3A%2F%2Fsportsbook.draftkings.com%2Fgateway%3Fs%3D356902394%26wpcid%3D239969%26wpcn%3DBet5Win150%26wpsrc%3DBetsperts%26wpcrid%3D%26wpscn%3D&pcid=239969&psn=Betsperts&pcn=Bet5Win150_4For4',
  'FanDuel':'https://wlfanduelus.adsrv.eacdn.com/C.ashx?btag=a_21545b_16c_&affid=11060&siteid=21545&adid=16&c=',
  'Bet365': 'https://www.bet365.com/olp/open-account?affiliate=365_02038136',
  'BetMGM': 'https://mediaserver.betmgmpartners.com/renderBanner.do?zoneId=1700764',
  'Caesars': 'https://wlwilliamhillus.adsrv.eacdn.com/C.ashx?btag=a_12260b_2091c_&affid=47&siteid=12260&adid=2091&c=',
  'Fanatics': 'https://go.affiliationcloud.com/0002JY',
};

// Mapping between dropdown labels and betTypeId values
const betTypeMapping = {
  Total: 3,
  Moneyline: 1,
  Spread: 2,
};

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const Odds = ({ bets, updatesPaused }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useRef(null);
  
const [sport, setSport] = useState(() => {
  const saved = localStorage.getItem('oddsSport');
  return saved || 'NFL';
});
const [betType, setBetType] = useState(() => {
  const saved = localStorage.getItem('oddsBetType');
  return saved || 'Summary';
});
const [periodType, setPeriodType] = useState(() => {
  const saved = localStorage.getItem('oddsPeriodType');
  return saved || 'Full Game';
});
  // Add state for current time to trigger re-renders for color updates
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Update current time every second for smooth color transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Add state for selected books
  const [selectedBooks, setSelectedBooks] = useState(() => {
    const saved = localStorage.getItem(`oddsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading selected books:', e);
      }
    }
    return staticBookmakers.filter(book => book !== 'Prophet Exchange' && book !== 'NoVig');
  });

  // Save selected books when they change
  useEffect(() => {
    localStorage.setItem(`oddsSelectedBooks_${isMobile ? 'mobile' : 'desktop'}`, JSON.stringify(selectedBooks));
  }, [selectedBooks, isMobile]);

  useEffect(() => {
  localStorage.setItem('oddsSport', sport);
}, [sport]);

useEffect(() => {
  localStorage.setItem('oddsBetType', betType);
}, [betType]);

useEffect(() => {
  localStorage.setItem('oddsPeriodType', periodType);
}, [periodType]);

  // Use static period types based on the selected sport
  const periodTypes = useMemo(() => {
    return periodTypesBySport[sport] || ['Full Game'];
  }, [sport]);

  // Reset periodType when sport changes if current periodType is not available
  useEffect(() => {
    if (!periodTypes.includes(periodType)) {
      setPeriodType(periodTypes[0] || 'Full Game');
    }
  }, [periodTypes, periodType]);

  // Handle books filter change
  const handleBooksFilterChange = (event) => {
    const value = event.target.value;
    if (value[value.length - 1] === 'all') {
      setSelectedBooks(selectedBooks.length === staticBookmakers.length ? [] : staticBookmakers);
    } else {
      setSelectedBooks(value);
    }
  };

  // Render value for books dropdown
  const renderBooksValue = (selected) => {
    if (!selected.length) return 'None';
    if (selected.length === staticBookmakers.length) return 'All';
    if (selected.length > 1) return `Multiple (${selected.length})`;
    return selected[0];
  };

  // Function to calculate opacity based on time difference (0-5 seconds scale)
const calculateOpacity = (oddsChangedDate) => {
  if (!oddsChangedDate) return 0;
  const changedTime = new Date(oddsChangedDate).getTime();
  const diffMs = currentTime - changedTime;
  const diffSeconds = diffMs / 1000;
  
  if (diffSeconds < 0) return 1;
  if (diffSeconds > 300) return 0; // 5 minutes ✅
  return 1 - (diffSeconds / 300); // ✅ Dividing by 300 seconds
};
  // Group bets into a single table
  const tableData = useMemo(() => {
    if (!Array.isArray(bets)) {
      console.log('Bets data is not an array:', bets);
      return { rows: [], columns: [], tableHeight: 0 };
    }

    const filteredBets = bets.filter(bet => {
      if (!bet) return false;
      const matchesSport = bet.leagueName === sport;
      const matchesCategory = bet.betCategory === 'Game Line';
      return matchesSport && matchesCategory;
    });

    console.log('Filtered bets for Odds table:', filteredBets.length, 'items');

    const eventGroups = {};
    filteredBets.forEach(bet => {
      const eventKey = bet.eventName;
      if (!eventGroups[eventKey]) {
        eventGroups[eventKey] = {
          event: bet.eventName,
          startTime: bet.eventStart,
          bets: [],
        };
      }
      eventGroups[eventKey].bets.push(bet);
    });

    const rowsWithStartTime = [];
    let rowId = 0;

    Object.entries(eventGroups).forEach(([eventKey, group]) => {
      const row = {
        id: rowId++,
        eventName: group.event,
        awayTeam: '',
        homeTeam: '',
        awayTeamLogo: '',
        homeTeamLogo: '',
        eventStart: group.startTime ? new Date(group.startTime) : new Date(),
        eventStartFormatted: {
          date: group.startTime
            ? new Date(group.startTime).toLocaleDateString('en-US', {
                year: '2-digit',
                month: 'numeric',
                day: 'numeric',
              })
            : '',
          time: group.startTime
            ? new Date(group.startTime).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '',
        },
        bookmakerOdds: {},
        bestOddsBookmakers: { '0': '', '1': '', 'Over': '', 'Under': '', 'topRow': '', 'bottomRow': '' },
      };

      const referenceBets = group.bets.filter(bet => 
        (bet.betTypeId === 1 || bet.betTypeId === 2 )
      );
      if (referenceBets.length > 0) {
        const awayBet = referenceBets.find(bet => bet.sideId === 0);
        const homeBet = referenceBets.find(bet => bet.sideId === 1);
        row.awayTeam = awayBet ? awayBet.teamName : 'Unknown Team A';
        row.homeTeam = homeBet ? homeBet.teamName : 'Unknown Team B';
        row.awayTeamLogo = awayBet && awayBet.teamLogo ? awayBet.teamLogo : '';
        row.homeTeamLogo = homeBet && homeBet.teamLogo ? homeBet.teamLogo : '';
      } else {
        const [parsedTeamA, parsedTeamB] = group.event.split(' @ ');
        const expectedAway = parsedTeamA ? parsedTeamA.trim().replace(/ -$/, '') : 'Unknown Team A';
        const expectedHome = parsedTeamB ? parsedTeamB.trim().replace(/ -$/, '') : 'Unknown Team B';
        row.awayTeam = expectedAway? expectedAway: 'Unknown Team A';
        row.homeTeam = expectedHome? expectedHome: 'Unknown Team B';
        row.awayTeamLogo = group.bets[0].awayLogo ? group.bets[0].awayLogo : '';
        row.homeTeamLogo = group.bets[0].homeLogo ? group.bets[0].homeLogo : '';
      }

      if (betType === 'Summary') {
        const spreadBets = group.bets.filter(bet => {
          const betTypeId = bet.betTypeId;
          const matchesBetType = betTypeId != null && betTypeId == 2;
          const matchesPeriodType = bet.periodTypeName === periodType;
          return matchesBetType && matchesPeriodType;
        });

        const totalBets = group.bets.filter(bet => {
          const betTypeId = bet.betTypeId;
          const matchesBetType = betTypeId != null && betTypeId == 3;
          const matchesPeriodType = bet.periodTypeName === periodType;
          return matchesBetType && matchesPeriodType;
        });

        const spreadOutcomes = {};
        const spreadDetails = {};
        const spreadUpdatedDates = {};
        spreadOutcomes['0'] = {};
        spreadOutcomes['1'] = {};
        spreadDetails['0'] = {};
        spreadDetails['1'] = {};
        spreadUpdatedDates['0'] = {};
        spreadUpdatedDates['1'] = {};
        
        spreadBets.forEach(bet => {
          const sideId = bet.sideId !== undefined ? bet.sideId.toString() : (bet.side === 'Home' ? '1' : '0');
          spreadOutcomes[sideId][bet.marketSourceName] = bet.price;
          spreadDetails[sideId][`${bet.marketSourceName}Points`] = bet.points;
          spreadDetails[sideId][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
          spreadDetails[sideId][`${bet.marketSourceName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
          spreadUpdatedDates[sideId][bet.marketSourceName] = bet.oddsChangedDate;
        });

        let favoriteId = '0';
        for (const book of staticBookmakers) {
          const awayPoints = spreadDetails['0'][`${book}Points`];
          const homePoints = spreadDetails['1'][`${book}Points`];
          if (awayPoints !== undefined && homePoints !== undefined) {
            favoriteId = awayPoints < 0 ? '0' : '1';
            break;
          }
        }

        const totalOutcomes = {};
        const totalDetails = {};
        const totalUpdatedDates = {};
        totalOutcomes['Over'] = {};
        totalDetails['Over'] = {};
        totalUpdatedDates['Over'] = {};
        
        totalBets.forEach(bet => {
          if (bet.side === 'Over') {
            totalOutcomes['Over'][bet.marketSourceName] = bet.price;
            totalDetails['Over'][`${bet.marketSourceName}Points`] = bet.points;
            totalDetails['Over'][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
            totalDetails['Over'][`${bet.marketSourceName}LineDisplay`] = `o${bet.points || ''}`;
            totalUpdatedDates['Over'][bet.marketSourceName] = bet.oddsChangedDate;
          }
        });

        const bestOdds = {};
        
        const favoriteSpreadEntries = [];
        selectedBooks.forEach(book => {
          const price = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
          const points = spreadDetails[favoriteId][`${book}Points`];
          if (price !== null) {
            favoriteSpreadEntries.push({
              book,
              price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: spreadDetails[favoriteId][`${book}LineDisplay`],
              logo: spreadDetails[favoriteId][`${book}Logo`],
            });
          }
        });
        
        const isSpreadTopRow = favoriteId === '0';
        const spreadRowKey = isSpreadTopRow ? 'topRow' : 'bottomRow';
        const totalRowKey = isSpreadTopRow ? 'bottomRow' : 'topRow';
        
        if (favoriteSpreadEntries.length > 0) {
          favoriteSpreadEntries.sort((a, b) => b.price - a.price);
          const bestEntry = favoriteSpreadEntries[0];
          bestOdds[spreadRowKey] = {
            price: bestEntry.price,
            lineDisplay: bestEntry.lineDisplay,
            logo: bestEntry.logo,
            display: `${bestEntry.lineDisplay} ${bestEntry.price > 0 ? '+' : ''}${bestEntry.price}`,
            type: 'spread'
          };
          row.bestOddsBookmakers[spreadRowKey] = bestEntry.book;
        } else {
          bestOdds[spreadRowKey] = { display: '', logo: '', type: 'spread' };
        }

        const overEntries = [];
        selectedBooks.forEach(book => {
          const price = totalOutcomes['Over'][book] ? parseFloat(totalOutcomes['Over'][book]) : null;
          const points = totalDetails['Over'][`${book}Points`];
          if (price !== null) {
            overEntries.push({
              book,
              price,
              points: points !== undefined ? parseFloat(points) : null,
              lineDisplay: totalDetails['Over'][`${book}LineDisplay`],
              logo: totalDetails['Over'][`${book}Logo`],
            });
          }
        });
        
        if (overEntries.length > 0) {
          overEntries.sort((a, b) => {
            if (a.points !== b.points) {
              return a.points - b.points;
            }
            return b.price - a.price;
          });
          const bestEntry = overEntries[0];
          bestOdds[totalRowKey] = {
            price: bestEntry.price,
            lineDisplay: bestEntry.lineDisplay,
            logo: bestEntry.logo,
            display: `${bestEntry.lineDisplay} ${bestEntry.price > 0 ? '+' : ''}${bestEntry.price}`,
            type: 'total'
          };
          row.bestOddsBookmakers[totalRowKey] = bestEntry.book;
        } else {
          bestOdds[totalRowKey] = { display: '', logo: '', type: 'total' };
        }

        row.summaryMetadata = {
          favoriteId,
          isSpreadTopRow
        };

        staticBookmakers.forEach(book => {
          const odds = {
            topRow: '',
            bottomRow: '',
            topRowUpdatedDate: null,
            bottomRowUpdatedDate: null,
          };
          
          if (isSpreadTopRow) {
            const spreadPrice = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (spreadPrice !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${spreadPrice > 0 ? '+' : ''}${spreadPrice}`;
              odds.topRowUpdatedDate = spreadUpdatedDates[favoriteId][book];
            }
            
            const overPrice = totalOutcomes['Over'][book] ? parseFloat(totalOutcomes['Over'][book]) : null;
            if (overPrice !== null) {
              const line = totalDetails['Over'][`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${overPrice > 0 ? '+' : ''}${overPrice}`;
              odds.bottomRowUpdatedDate = totalUpdatedDates['Over'][book];
            }
          } else {
            const overPrice = totalOutcomes['Over'][book] ? parseFloat(totalOutcomes['Over'][book]) : null;
            if (overPrice !== null) {
              const line = totalDetails['Over'][`${book}LineDisplay`] || '';
              odds.topRow = `${line} ${overPrice > 0 ? '+' : ''}${overPrice}`;
              odds.topRowUpdatedDate = totalUpdatedDates['Over'][book];
            }
            
            const spreadPrice = spreadOutcomes[favoriteId][book] ? parseFloat(spreadOutcomes[favoriteId][book]) : null;
            if (spreadPrice !== null) {
              const line = spreadDetails[favoriteId][`${book}LineDisplay`] || '';
              odds.bottomRow = `${line} ${spreadPrice > 0 ? '+' : ''}${spreadPrice}`;
              odds.bottomRowUpdatedDate = spreadUpdatedDates[favoriteId][book];
            }
          }
          
          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        row.bestOdds = bestOdds;
        
      } else {
        const oddsBets = group.bets.filter(bet => {
          const betTypeId = bet.betTypeId;
          const expectedBetTypeId = betTypeMapping[betType];
          const matchesBetType = betTypeId != null && betTypeId == expectedBetTypeId;
          const matchesPeriodType = bet.periodTypeName === periodType;
          return matchesBetType && matchesPeriodType;
        });

        const outcomes = {};
        const outcomeDetails = {};
        const outcomeUpdatedDates = {};
        if (betType === 'Moneyline' || betType === 'Spread') {
          outcomes['0'] = {};
          outcomes['1'] = {};
          outcomeDetails['0'] = {};
          outcomeDetails['1'] = {};
          outcomeUpdatedDates['0'] = {};
          outcomeUpdatedDates['1'] = {};
          oddsBets.forEach(bet => {
            const sideId = bet.sideId !== undefined ? bet.sideId.toString() : (bet.side === 'Home' ? '1' : '0');
            outcomes[sideId][bet.marketSourceName] = bet.price;
            outcomeDetails[sideId][`${bet.marketSourceName}Points`] = bet.points;
            outcomeDetails[sideId][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
            outcomeDetails[sideId][`${bet.marketSourceName}OutcomeName`] = bet.side || (sideId === '0' ? 'Away' : 'Home');
            outcomeDetails[sideId][`${bet.marketSourceName}LineDisplay`] = '';
            outcomeUpdatedDates[sideId][bet.marketSourceName] = bet.oddsChangedDate;
            if (betType === 'Spread') {
              outcomeDetails[sideId][`${bet.marketSourceName}LineDisplay`] = `${bet.points > 0 ? '+' : ''}${bet.points}`;
            }
          });
        } else {
          outcomes['Over'] = {};
          outcomes['Under'] = {};
          outcomeDetails['Over'] = {};
          outcomeDetails['Under'] = {};
          outcomeUpdatedDates['Over'] = {};
          outcomeUpdatedDates['Under'] = {};
          oddsBets.forEach(bet => {
            const outcome = bet.side === 'Over' ? 'Over' : 'Under';
            outcomes[outcome][bet.marketSourceName] = bet.price;
            outcomeDetails[outcome][`${bet.marketSourceName}Points`] = bet.points;
            outcomeDetails[outcome][`${bet.marketSourceName}Logo`] = bookmakerLogos[bet.marketSourceName] || '';
            outcomeDetails[outcome][`${bet.marketSourceName}OutcomeName`] = bet.side || outcome;
            outcomeDetails[outcome][`${bet.marketSourceName}LineDisplay`] = `${outcome === 'Over' ? 'o' : 'u'}${bet.points || ''}`;
            outcomeUpdatedDates[outcome][bet.marketSourceName] = bet.oddsChangedDate;
          });
        }

        const bestOdds = {};
        let bestOddsKeys;
        if (betType === 'Moneyline' || betType === 'Spread') {
          bestOddsKeys = ['0', '1'];
        } else {
          bestOddsKeys = ['Over', 'Under'];
        }

        bestOddsKeys.forEach(outcome => {
          const entries = [];
          selectedBooks.forEach(book => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            const points = outcomeDetails[outcome][`${book}Points`];
            if (price !== null) {
              entries.push({
                book,
                price,
                points: points !== undefined ? parseFloat(points) : null,
                logo: outcomeDetails[outcome][`${book}Logo`],
                outcomeName: outcomeDetails[outcome][`${book}OutcomeName`],
                lineDisplay: outcomeDetails[outcome][`${book}LineDisplay`],
              });
            }
          });

          let bestEntry = null;
          if (entries.length > 0) {
            if (betType === 'Total') {
              entries.sort((a, b) => {
                if (a.points !== b.points) {
                  return outcome === 'Over' ? a.points - b.points : b.points - a.points;
                }
                return b.price - a.price;
              });
              bestEntry = entries[0];
            } else {
              entries.sort((a, b) => b.price - a.price);
              bestEntry = entries[0];
            }
          }

          if (bestEntry) {
            bestOdds[outcome] = {
              price: bestEntry.price,
              points: bestEntry.points,
              logo: bestEntry.logo,
              outcomeName: bestEntry.outcomeName,
              lineDisplay: bestEntry.lineDisplay,
            };
            row.bestOddsBookmakers[outcome] = bestEntry.book;
          } else {
            bestOdds[outcome] = {
              price: null,
              points: null,
              logo: '',
              outcomeName: outcome === '0' ? 'Away' : 'Home',
              lineDisplay: '',
            };
            row.bestOddsBookmakers[outcome] = '';
          }
        });

        staticBookmakers.forEach(book => {
          const odds = {
            updatedDates: {}
          };
          bestOddsKeys.forEach(outcome => {
            const price = outcomes[outcome][book] ? parseFloat(outcomes[outcome][book]) : null;
            if (price !== null) {
              const formattedPrice = (betType === 'Moneyline' || betType === 'Total') && price > 0 ? `+${price}` : `${price}`;
              const line = outcomeDetails[outcome][`${book}LineDisplay`] || '';
              odds[outcome] = line ? `${line} ${formattedPrice}` : formattedPrice;
              odds.updatedDates[outcome] = outcomeUpdatedDates[outcome][book];
            } else {
              odds[outcome] = '';
              odds.updatedDates[outcome] = null;
            }
          });
          row.bookmakerOdds[book] = odds;
          row[book] = '';
        });

        bestOddsKeys.forEach(outcome => {
          const { price, lineDisplay } = bestOdds[outcome] || {};
          if (price !== null) {
            const formattedPrice = (betType === 'Moneyline' || betType === 'Total') && price > 0 ? `+${price}` : `${price}`;
            bestOdds[outcome].display = lineDisplay ? `${lineDisplay} ${formattedPrice}` : formattedPrice;
          } else {
            bestOdds[outcome].display = '';
          }
        });

        row.bestOdds = bestOdds;
      }

      rowsWithStartTime.push(row);
    });

    rowsWithStartTime.sort((a, b) => a.eventStart - b.eventStart);

    const rowHeight = 70;
    const headerHeight = 56;
    const headerBorderHeight = 2;
    const minHeight = headerHeight + headerBorderHeight + (rowHeight * 2);
    const calculatedHeight = (rowsWithStartTime.length * rowHeight) + headerHeight + headerBorderHeight;
    const tableHeight = Math.max(minHeight, calculatedHeight);

    return { rows: rowsWithStartTime, columns: [], tableHeight };
  }, [bets, sport, betType, periodType, selectedBooks]);

  const getSavedColumnOrder = () => {
    try {
      const key = `oddsColumnOrder_${sport}_${periodType}_${isMobile ? 'mobile' : 'desktop'}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column order:', error);
    }
    return null;
  };

  const saveColumnOrder = (order) => {
    try {
      const key = `oddsColumnOrder_${sport}_${betType}_${periodType}_${isMobile ? 'mobile' : 'desktop'}`;
      localStorage.setItem(key, JSON.stringify(order));
    } catch (error) {
      console.error('Error saving column order:', error);
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef(null);

  const handleBookmakerClick = (event, bookmakerName) => {
    if (!isDragging) {
      const link = bookmakerLinks[bookmakerName];
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'eventName',
        headerName: '',
        minWidth: isMobile ? 35 : 230,
        width: isMobile ? 35 : undefined,
        flex: isMobile ? 1 : 1,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
              {params.row.awayTeamLogo && (
                <img
                  src={params.row.awayTeamLogo}
                  alt={`${params.row.awayTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }}
                />
              )}
              {!isMobile && <Typography sx={{ fontSize: '14px' }}>{params.row.awayTeam}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingLeft: isMobile ? '4px' : '8px', paddingRight: isMobile ? '0px' : '0' }}>
              {params.row.homeTeamLogo && (
                <img
                  src={params.row.homeTeamLogo}
                  alt={`${params.row.homeTeam} Logo`}
                  style={{ width: '24px', height: '24px', objectFit: 'contain', marginRight: isMobile ? '0' : '8px' }}
                />
              )}
              {!isMobile && <Typography sx={{ fontSize: '14px' }}>{params.row.homeTeam}</Typography>}
            </Box>
          </Box>
        ),
      },
      {
        field: 'eventStartFormatted',
        headerName: '',
        minWidth: isMobile ? 70 : 85,
        maxWidth: isMobile ? 70 : 85,
        sortable: false,
        disableReorder: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '0', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.date}</Typography>
            </Box>
            <Box sx={{ height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' }}>
              <Typography sx={{ fontSize: isMobile ? '12px' : '14px' }}>{params.value.time}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: 'bestOdds',
        headerName: 'Best Odds',
        headerAlign: 'right',
        minWidth: isMobile ? 110 : 135,
        flex: 1,
        sortable: false,
        disableReorder: true,
        renderHeader: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', paddingRight: isMobile ? '4px' : '8px' }}>
            <Typography sx={{ fontSize: isMobile ? '14px' : '16px', color: '#ffffff' }}>Best Odds</Typography>
          </Box>
        ),
        renderCell: (params) => {
          const value = params.value;
          
          if (betType === 'Summary') {
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px' }}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      textAlign: 'right',
                      pr: isMobile ? '4px' : '8px',
                      flex: 1,
                    }}
                  >
                    {value.topRow?.display || ''}
                  </Typography>
                  {value.topRow?.logo && (
                    <img
                      src={value.topRow.logo}
                      alt="Best Odds Top"
                      style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px' }}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      textAlign: 'right',
                      pr: isMobile ? '4px' : '8px',
                      flex: 1,
                    }}
                  >
                    {value.bottomRow?.display || ''}
                  </Typography>
                  {value.bottomRow?.logo && (
                    <img
                      src={value.bottomRow.logo}
                      alt="Best Odds Bottom"
                      style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                    />
                  )}
                </Box>
              </Box>
            );
          } else {
            const outcomes = betType === 'Moneyline' || betType === 'Spread' ? ['0', '1'] : ['Over', 'Under'];
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                {outcomes.map(outcome => {
                  const { display, logo, outcomeName } = value[outcome] || {};
                  if (!display) return null;
                  return (
                    <Box key={outcome} sx={{ display: 'flex', alignItems: 'center', height: '35px', paddingRight: '8px' }}>
                      <Typography
                        sx={{
                          fontSize: isMobile ? '12px' : '14px',
                          textAlign: 'right',
                          pr: isMobile ? '4px' : '8px',
                          flex: 1,
                        }}
                      >
                        {display}
                      </Typography>
                      {logo && (
                        <img
                          src={logo}
                          alt={`${outcomeName} Logo`}
                          style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          }
        },
      },
    ];

    const visibleBookmakers = selectedBooks;
    
    const bookmakerColumns = {};
    visibleBookmakers.forEach(book => {
      bookmakerColumns[book] = {
        field: book,
        headerName: '',
        headerAlign: 'right',
        minWidth: isMobile ? 90 : 100,
        flex: 1,
        sortable: false,
        disableReorder: false,
        renderHeader: () => {
          const hasLink = !!bookmakerLinks[book];
          
          return (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                width: '100%', 
                height: '100%',
                paddingRight: isMobile ? '4px' : '12px',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                userSelect: 'none',
              }}
              onClick={(e) => {
                if (hasLink && e.detail > 0) {
                  const link = bookmakerLinks[book];
                  if (link) {
                    window.open(link, '_blank', 'noopener,noreferrer');
                  }
                }
              }}
            >
              {bookmakerLogos[book] ? (
                <img
                  src={bookmakerLogos[book]}
                  alt={book}
                  style={{ 
                    width: isMobile ? '50px' : '70px', 
                    height: 'auto', 
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                />
              ) : (
                <Box sx={{ width: isMobile ? '50px' : '70px', height: 'auto' }} />
              )}
            </Box>
          );
        },
        renderCell: (params) => {
          const row = params.row;
          const bookOdds = row.bookmakerOdds && row.bookmakerOdds[params.field];
          const bestBookmaker = row.bestOddsBookmakers;
          
          if (betType === 'Summary') {
            if (bookOdds) {
              const topRowOpacity = calculateOpacity(bookOdds.topRowUpdatedDate);
              const bottomRowOpacity = calculateOpacity(bookOdds.bottomRowUpdatedDate);
              
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                  <Box
                    sx={{
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      border: params.field === bestBookmaker['topRow'] ? '2px solid #00cc00' : 'none',
                      borderRadius: '0',
                      width: '100%',
                      backgroundColor: `rgba(13, 71, 161, ${topRowOpacity})`,
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '12px',
                        flex: 1,
                      }}
                    >
                      {bookOdds.topRow || ''}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      border: params.field === bestBookmaker['bottomRow'] ? '2px solid #00cc00' : 'none',
                      borderRadius: '0',
                      width: '100%',
                      backgroundColor: `rgba(13, 71, 161, ${bottomRowOpacity})`,
                      transition: 'background-color 0.3s ease',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? '12px' : '14px',
                        textAlign: 'right',
                        pr: isMobile ? '4px' : '12px',
                        flex: 1,
                      }}
                    >
                      {bookOdds.bottomRow || ''}
                    </Typography>
                  </Box>
                </Box>
              );
            }
            return <Box sx={{ textAlign: 'right', pr: '12px' }}><Typography sx={{ fontSize: '14px' }}></Typography></Box>;
          } else {
            if (bookOdds) {
              const outcomes = betType === 'Moneyline' || betType === 'Spread' ? ['0', '1'] : ['Over', 'Under'];
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' }}>
                  {outcomes.map(outcome => {
                    const opacity = calculateOpacity(bookOdds.updatedDates?.[outcome]);
                    
                    return (
                      <Box
                        key={outcome}
                        sx={{
                          height: '35px',
                          display: 'flex',
                          alignItems: 'center',
                          border: params.field === bestBookmaker[outcome] ? '2px solid #00cc00' : 'none',
                          borderRadius: '0',
                          width: '100%',
                          backgroundColor: `rgba(13, 71, 161,  ${opacity})`,
                          transition: 'background-color 0.3s ease',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: isMobile ? '12px' : '14px',
                            textAlign: 'right',
                            pr: isMobile ? '4px' : '12px',
                            flex: 1,
                          }}
                        >
                          {bookOdds[outcome] || ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            }
            return <Box sx={{ textAlign: 'right', pr: '12px' }}><Typography sx={{ fontSize: '14px' }}></Typography></Box>;
          }
        },
      };
    });

    const savedOrder = getSavedColumnOrder();
    const finalColumns = [...baseColumns];
    
    if (savedOrder && savedOrder.bookmakers) {
      savedOrder.bookmakers.forEach(book => {
        if (bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
      
      visibleBookmakers.forEach(book => {
        if (!savedOrder.bookmakers.includes(book) && bookmakerColumns[book]) {
          finalColumns.push(bookmakerColumns[book]);
        }
      });
    } else {
      visibleBookmakers.forEach(book => {
        finalColumns.push(bookmakerColumns[book]);
      });
    }

    return finalColumns;
  }, [sport, betType, periodType, isMobile, selectedBooks, currentTime]);

  const handleColumnOrderChange = (params) => {
    if (apiRef.current) {
      const state = apiRef.current.state;
      const orderedFields = state.columns.orderedFields || state.columns.all;
      
      if (orderedFields) {
        const fixedColumns = ['eventName', 'eventStartFormatted', 'bestOdds'];
        const bookmakers = orderedFields.filter(field => !fixedColumns.includes(field));
        
        saveColumnOrder({
          fixed: fixedColumns,
          bookmakers: bookmakers
        });
      }
    }
  };

  useEffect(() => {
    if (apiRef.current && columns.length > 0) {
      const savedOrder = getSavedColumnOrder();
      if (savedOrder && savedOrder.bookmakers) {
        const fullOrder = [...savedOrder.fixed, ...savedOrder.bookmakers.filter(book => selectedBooks.includes(book))];
        
        setTimeout(() => {
          try {
            if (apiRef.current && apiRef.current.setColumnOrder) {
              apiRef.current.setColumnOrder(fullOrder);
            }
          } catch (error) {
            console.error('Error restoring column order:', error);
          }
        }, 100);
      }
    }
  }, [sport, betType, periodType, isMobile, columns, selectedBooks]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: 'background.default' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px' }}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 1 : 2} 
              alignItems={isMobile ? "stretch" : "center"}
            >
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="sport-filter-label" shrink sx={{ color: '#ffffff' }}>League</InputLabel>
                <Select
                  labelId="sport-filter-label"
                  label="League"
                  notched
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                >
                  <MenuItem value="MLB">MLB</MenuItem>
                  <MenuItem value="NBA">NBA</MenuItem> 
                  <MenuItem value="CBB">CBB</MenuItem>            
                  <MenuItem value="WNBA">WNBA</MenuItem>          
                  <MenuItem value="NFL">NFL</MenuItem>
                  <MenuItem value="CFB">CFB</MenuItem>
                  <MenuItem value="NHL">NHL</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="bet-type-filter-label" shrink sx={{ color: '#ffffff' }}>Bet Type</InputLabel>
                <Select
                  labelId="bet-type-filter-label"
                  label="Bet Type"
                  notched
                  value={betType}
                  onChange={(e) => setBetType(e.target.value)}
                >
                  {betTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
                <InputLabel id="period-type-filter-label" shrink sx={{ color: '#ffffff' }}>Period Type</InputLabel>
                <Select
                  labelId="period-type-filter-label"
                  label="Period Type"
                  notched
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                >
                  {periodTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: isMobile ? '100%' : 140 }}>
                <InputLabel id="books-filter-label" shrink sx={{ color: '#ffffff' }}>Books</InputLabel>
                <Select
                  multiple
                  labelId="books-filter-label"
                  label="Books"
                  notched
                  value={selectedBooks}
                  onChange={handleBooksFilterChange}
                  renderValue={renderBooksValue}
                >
                  <MenuItem value="all">
                    <Checkbox checked={selectedBooks.length === staticBookmakers.length} />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {staticBookmakers.map((book) => (
                    <MenuItem key={book} value={book}>
                      <Checkbox checked={selectedBooks.includes(book)} />
                      <ListItemText primary={book} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>
          <Box 
            className="table-section"
            sx={{
              height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 88px)',
              overflowY: 'auto',
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              {tableData.rows.length === 0 ? (
                <Box className="empty-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">
                    No odds data available for the selected filters.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: '100%', width: '100%' }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={tableData.rows}
                    columns={columns}
                    disableSelectionOnClick
                    disableRowSelectionOnClick
                    disableColumnSort
                    disableColumnMenu
                    disableColumnSelector
                    disableColumnFilter
                    hideFooter
                    rowHeight={70}
                    onColumnOrderChange={handleColumnOrderChange}
                    sx={{
                      '& .MuiDataGrid-columnHeaders': { 
                        backgroundColor: '#1e1e1e', 
                        borderBottom: '2px solid #444',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                      },
                      '& .MuiDataGrid-columnHeader': {
                        userSelect: 'none',
                        cursor: 'move',
                        outline: 'none !important',
                        '&:focus': {
                          outline: 'none !important',
                        },
                        '&:focus-within': {
                          outline: 'none !important',
                        },
                      },
                      '& .MuiDataGrid-columnHeader--alignRight': {
                        justifyContent: 'flex-end !important',
                      },
                      '& .MuiDataGrid-columnHeaderTitleContainer': {
                        outline: 'none !important',
                        '&:focus': {
                          outline: 'none !important',
                        },
                      },
                      '& .MuiDataGrid-cell': {
                        color: '#ffffff !important',
                        userSelect: 'none',
                        padding: 0,
                        borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-cell:last-child': {
                        borderRight: 'none',
                      },
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                      },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                      '& .MuiDataGrid-columnSeparator': { 
                        visibility: 'visible',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        overflowY: 'auto !important',
                      },
                      '& .MuiDataGrid-root': {
                        overflowY: 'auto !important',
                        margin: 0,
                      },
                      '& .MuiDataGrid-main': {
                        padding: 0,
                      },
                      '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                      '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important' },
                      '& .MuiSelect-icon': { color: '#ffffff !important' },
                      '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                      '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#ffffff !important',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: 1.2,
                      },
                      '& .MuiDataGrid-columnHeaderDraggableContainer': {
                        cursor: 'move',
                      },
                    }}
                    rowBuffer={5}
                    rowThreshold={2}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Odds;